import React, { useRef, useEffect, useState } from 'react';
import Autocomplete from 'react-google-autocomplete';

// Declare google property on window for TypeScript
declare global {
  interface Window {
    google: any;
  }
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  disabled?: boolean;
  required?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  required = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [coordinates, setCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  
  // Check if Google Maps API key is available and valid
  const hasValidApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY && 
                         process.env.REACT_APP_GOOGLE_MAPS_API_KEY !== 'your-google-maps-api-key' &&
                         process.env.REACT_APP_GOOGLE_MAPS_API_KEY.length > 10;

  useEffect(() => {
    if (hasValidApiKey && !window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsGoogleMapsLoaded(true);
      document.head.appendChild(script);
    } else if (window.google) {
      setIsGoogleMapsLoaded(true);
    }
  }, [hasValidApiKey]);

  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  // If disabled or no valid API key, show regular input
  if (disabled || !hasValidApiKey || !isGoogleMapsLoaded) {
    return (
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={required}
          style={{
            width: '500px',
            padding: '12px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: disabled ? '#f9fafb' : 'white',
            color: disabled ? '#6b7280' : '#111827'
          }}
          placeholder={!isGoogleMapsLoaded && hasValidApiKey ? "Loading address autocomplete..." : "Enter address manually"}
        />
        {!hasValidApiKey && !disabled && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
            maxWidth: '200px'
          }}>
            ⚠️ Google Maps autocomplete unavailable. Please enter address manually.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
      <Autocomplete
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        onPlaceSelected={(place) => {
          if (place.formatted_address) {
            onChange(place.formatted_address);
            if (place.geometry?.location) {
              setCoordinates({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              });
            }
          }
        }}
        options={{
          types: ['address'],
          componentRestrictions: { country: ['nz', 'au'] },
        }}
        style={{
          width: '500px',
          padding: '12px 16px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: 'white',
          color: '#111827'
        }}
        defaultValue={value}
      />
      {coordinates && (
        <div style={{ 
          width: '300px', 
          height: '200px', 
          border: '1px solid #d1d5db', 
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginLeft: '20px'
        }}>
          <img 
            src={`https://maps.googleapis.com/maps/api/staticmap?center=${coordinates.lat},${coordinates.lng}&zoom=15&size=300x200&maptype=roadmap&markers=color:red%7C${coordinates.lat},${coordinates.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
            alt="Location map"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
